import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarBlank, CircleNotch, EnvelopeSimple, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DownloadFlashPdfButton } from "@/components/DownloadFlashPdfButton";
import { api } from "@/lib/api";
import { defaultRange, describeRange, rangePresets, toYmd } from "@/lib/weekEnding";

// Custom date range + PDF download + editable email draft for the Flash Report.
export function FlashReportActions({ clientId, label = "", testid, className = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(defaultRange);
  const [creating, setCreating] = useState(false);
  const ready = range?.from && range?.to;
  const params = ready ? { date_from: toYmd(range.from), date_to: toYmd(range.to) } : null;
  const now = new Date();

  const draftEmail = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/flash-email/drafts`, params);
      navigate(`/admin/email-drafts/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the email draft");
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" data-testid={testid} title="Flash Report — PDF or email"
          className={`rounded-md border-[#E2E8F0] text-xs ${className}`}>
          <FileText size={13} />{label && <span className="ml-1.5">{label}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[330px] p-0" data-testid="flash-report-popover">
        <div className="px-4 pt-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#475569] flex items-center gap-1.5">
            <CalendarBlank size={12} /> Report period
          </p>
          <p className="font-mono-num text-xs text-[#0F172A] mt-1" data-testid="range-summary">{describeRange(range || {})}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {rangePresets().map((p) => (
              <button key={p.key} type="button" onClick={() => setRange({ from: p.from, to: p.to })} data-testid={`range-preset-${p.key}`}
                className="rounded-full border border-[#E2E8F0] px-2.5 py-1 text-[11px] text-[#334155] hover:bg-[#EEEEEE] transition-colors">
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <Calendar mode="range" weekStartsOn={1} selected={range} onSelect={setRange}
          defaultMonth={range?.to || range?.from} disabled={{ after: now }} data-testid="range-calendar" />
        <div className="grid grid-cols-2 gap-2 border-t border-[#E2E8F0] p-3">
          <DownloadFlashPdfButton clientId={clientId} params={params} label="PDF" testid={`${testid}-pdf`} className="h-9" />
          <Button size="sm" variant="outline" className="h-9 rounded-md border-[#E2E8F0] text-xs" data-testid={`${testid}-email`}
            disabled={!ready || creating} onClick={draftEmail}>
            {creating ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <EnvelopeSimple size={13} className="mr-1.5" />}
            Draft email
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
